import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';

import {AppDispatchType} from 'app/store/AppStore';

import type {
  BaseDocumentContentType,
  BaseDocumentVersionContentType,
} from 'sweet-types';

export interface ToastMessageLinkType {
  text: string;
  to: string;
}

export interface ToastMessageShapeType {
  messageId: number | string;
  text: string;
  link?: ToastMessageLinkType;
  level: 'info' | 'error' | 'warning' | 'success';
  expirationTime: number;
}

export type ToastMessageOptionsType = {
  link?: ToastMessageLinkType;
  level?: ToastMessageShapeType['level'];
  timeout?: number;
};

export enum UPGRADE_MODALS {
  PROCEDURE_LIMIT = 'PROCEDURE_LIMIT',
  USER_LIMIT = 'USER_LIMIT',
  TEAM_LIMIT = 'TEAM_LIMIT',
  MANAGER_LIMIT = 'MANAGER_LIMIT',
  CREATE_KB = 'CREATE_KB',
  SETUP_TEAM_SIGNOFF = 'SETUP_TEAM_SIGNOFF',
  ASSIGN_TO_ROLE = 'ASSIGN_TO_ROLE',
  SUDO_REQUIRED = 'SUDO_REQUIRED',
}

export function isUpgradeModal(text: string): boolean {
  return Object.values(UPGRADE_MODALS).includes(text as UPGRADE_MODALS);
}

export function hasUpgradeModalError(error: unknown): boolean {
  // checks if any error codes in the error are upgrade modal errors.
  if (!error || typeof error !== 'object' || Array.isArray(error)) {
    return false;
  }
  return Object.values(error as Record<string, unknown>).some(
    (codes) => Array.isArray(codes) && codes.some(isUpgradeModal)
  );
}

export const NOTIFICATION_DEFAULT_TIMEOUT = 5000;

export interface ToastsState {
  messages: Array<ToastMessageShapeType>;
  isHovering: boolean;
  showingUpgradeModal: null | UPGRADE_MODALS;
  isDeletingDocument: boolean;
  hiddenWarnings: {
    largeContent: Record<string, boolean>;
  };
}

const initialState: ToastsState = {
  messages: [],
  isHovering: false,
  showingUpgradeModal: null,
  isDeletingDocument: false,
  hiddenWarnings: {largeContent: {}},
};

const toastsSlice = createSlice({
  name: 'toasts',
  initialState,
  reducers: {
    showToastNotification: (
      state,
      action: PayloadAction<ToastMessageShapeType>
    ) => {
      state.messages.push(action.payload);
    },
    hideNotification: (state, action: PayloadAction<number | string>) => {
      state.messages = state.messages.filter(
        (m) => m.messageId != action.payload
      );
    },
    hideExpiredNotifications: (state) => {
      if (state.isHovering) {
        return;
      }
      const nowTs = new Date().getTime();
      state.messages = state.messages.filter((m) => m.expirationTime > nowTs);
    },
    toggleIsHovering: (state) => {
      state.isHovering = !state.isHovering;
    },
    showUpgradeModal: (state, action: PayloadAction<null | UPGRADE_MODALS>) => {
      state.showingUpgradeModal = action.payload;
    },
    setDeletingDocumentFlag: (state, action: PayloadAction<boolean>) => {
      state.isDeletingDocument = action.payload;
    },
    setLargeContentWarningVisibility: (
      state,
      {
        payload: {contentType, id, visible},
      }: PayloadAction<{
        contentType: BaseDocumentVersionContentType | BaseDocumentContentType;
        id: number;
        visible: boolean;
      }>
    ) => {
      state.hiddenWarnings.largeContent[`${contentType}:${id}`] = visible;
    },
  },
});

export const toastsReducer = toastsSlice.reducer;

export const {
  hideNotification,
  toggleIsHovering,
  showUpgradeModal,
  setDeletingDocumentFlag,
  setLargeContentWarningVisibility,
} = toastsSlice.actions;

export const showToastNotification =
  (
    /* a modern version of showNotification which accept a link button to be
    shown in the notification box */
    text: string,
    {
      link = undefined,
      level = 'info',
      timeout = NOTIFICATION_DEFAULT_TIMEOUT,
    }: ToastMessageOptionsType = {
      link: undefined,
      level: 'info',
      timeout: NOTIFICATION_DEFAULT_TIMEOUT,
    }
  ) =>
  (dispatch: AppDispatchType) => {
    if (text !== '' && !isUpgradeModal(text)) {
      dispatch(
        toastsSlice.actions.showToastNotification({
          text,
          link,
          level,
          messageId: nanoid(),
          expirationTime: new Date().getTime() + timeout,
        })
      );
    }

    setTimeout(
      () => dispatch(toastsSlice.actions.hideExpiredNotifications()),
      timeout
    );
  };

export const showNotification =
  (text: string, level?: ToastMessageShapeType['level'], timeout?: number) =>
  (dispatch: AppDispatchType) =>
    dispatch(showToastNotification(text, {level, timeout}));
